import { habilidadesDe, type ChaveHabilidade } from './habilidades';
import type { Rng } from './rng';
import type { Clube, Jogador, Posicao } from './tipos';

/**
 * Treino (base própria; o original tem um botão Treino que não foi decodificado). Cada rodada o clube treina:
 *  - foco do time: uma habilidade (ou físico, que repõe energia e protege de lesões, ou equilibrado);
 *  - intensidade: leve, normal ou pesada (mais ganho, mais cansaço e risco de lesão);
 *  - treino individual: até 5 jogadores com uma habilidade escolhida (ganho bem maior).
 * Cada ganho sobe a habilidade em 1; a cada 5 ganhos o jogador também ganha +1 de força (overall).
 */

export type FocoTreino = ChaveHabilidade | 'fisico' | 'equilibrado';
export type IntensidadeTreino = 'leve' | 'normal' | 'pesada';

export interface Treino {
  foco: FocoTreino;
  intensidade: IntensidadeTreino;
  individuais: { jogadorId: number; habilidade: ChaveHabilidade }[];
}

export const MAX_INDIVIDUAIS = 5;
export const TREINO_PADRAO = (): Treino => ({ foco: 'equilibrado', intensidade: 'normal', individuais: [] });

const HABILIDADES: ChaveHabilidade[] = ['gol', 'des', 'arm', 'fin', 'vel', 'tec', 'pas'];
const FOCOS: readonly FocoTreino[] = [...HABILIDADES, 'fisico', 'equilibrado'];
const INTENSIDADES: readonly IntensidadeTreino[] = ['leve', 'normal', 'pesada'];

const CHANCE: Record<IntensidadeTreino, number> = { leve: 0.03, normal: 0.05, pesada: 0.085 };
const CANSACO: Record<IntensidadeTreino, number> = { leve: 0, normal: 1, pesada: 4 };
const RISCO_LESAO: Record<IntensidadeTreino, number> = { leve: 0, normal: 0.001, pesada: 0.008 };

/** Habilidades que fazem sentido treinar em cada posição (usadas no treino "equilibrado"). */
const FOCO_DA_POSICAO: Record<Posicao, ChaveHabilidade[]> = {
  G: ['gol', 'tec', 'pas'],
  Z: ['des', 'vel', 'pas'],
  L: ['vel', 'des', 'pas', 'tec'],
  M: ['arm', 'pas', 'tec', 'des'],
  A: ['fin', 'vel', 'tec'],
};

export function normalizarTreino(clube: Clube, t: Partial<Treino> | undefined): Treino {
  const ids = new Set(clube.elenco.map((j) => j.id));
  const foco = t?.foco ?? 'equilibrado';
  const intensidade = t?.intensidade ?? 'normal';
  if (!FOCOS.includes(foco)) throw new Error('foco de treino inválido');
  if (!INTENSIDADES.includes(intensidade)) throw new Error('intensidade de treino inválida');
  const individuais: Treino['individuais'] = [];
  const lista = t?.individuais ?? [];
  if (!Array.isArray(lista) || lista.length > MAX_INDIVIDUAIS) throw new Error(`no máximo ${MAX_INDIVIDUAIS} treinos individuais`);
  const usados = new Set<number>();
  for (const x of lista) {
    if (!x || !Number.isInteger(x.jogadorId) || !ids.has(x.jogadorId)) throw new Error('treino individual: jogador inválido');
    if (!HABILIDADES.includes(x.habilidade)) throw new Error('treino individual: habilidade inválida');
    if (usados.has(x.jogadorId)) throw new Error('o mesmo jogador aparece duas vezes no treino individual');
    usados.add(x.jogadorId);
    individuais.push({ jogadorId: x.jogadorId, habilidade: x.habilidade });
  }
  return { foco, intensidade, individuais };
}

function fatorIdade(idade: number): number {
  if (idade <= 21) return 1.8;
  if (idade <= 25) return 1.2;
  if (idade >= 34) return 0.25;
  if (idade >= 31) return 0.5;
  return 1;
}

/** Treina o clube por uma rodada. Devolve o que valeu aviso (jogador que ganhou força ou se machucou treinando). */
export function treinarClube(c: Clube, rng: Rng): string[] {
  const t = c.treino ?? TREINO_PADRAO();
  const avisos: string[] = [];
  const individual = new Map(t.individuais.map((x) => [x.jogadorId, x.habilidade]));
  for (const j of c.elenco) {
    const h = habilidadesDe(j);
    // cansaço e recuperação
    if (t.foco === 'fisico') j.energia = Math.min(100, j.energia + 4);
    else if (CANSACO[t.intensidade] > 0) j.energia = Math.max(30, j.energia - CANSACO[t.intensidade]);
    // lesão por treino pesado (o treino físico protege)
    const risco = RISCO_LESAO[t.intensidade] * (t.foco === 'fisico' ? 0.4 : 1);
    if (j.lesaoRodadas <= 0 && risco > 0 && rng.chance(risco)) {
      j.lesaoRodadas = rng.faixa(1, 2);
      if (c.humano) avisos.push(`${j.nome} se machucou no treino (fora por ${j.lesaoRodadas} rodada${j.lesaoRodadas > 1 ? 's' : ''}).`);
      continue;
    }
    if (t.foco === 'fisico' && !individual.has(j.id)) continue;
    const alvo = individual.get(j.id);
    let chave: ChaveHabilidade;
    let chance = CHANCE[t.intensidade] * fatorIdade(j.idade);
    if (alvo) {
      chave = alvo;
      chance *= 2.8;
    } else if (t.foco === 'equilibrado') {
      const opcoes = FOCO_DA_POSICAO[j.posicao];
      chave = opcoes[rng.inteiro(opcoes.length)] as ChaveHabilidade;
    } else {
      chave = t.foco as ChaveHabilidade;
      chance *= 1.5;
      // treinar goleiro só faz sentido para goleiros e vice-versa
      if ((chave === 'gol') !== (j.posicao === 'G')) chance *= 0.2;
    }
    if (h[chave] >= 99 || !rng.chance(Math.min(0.5, chance))) continue;
    h[chave]++;
    j.progressoTreino = (j.progressoTreino ?? 0) + 0.2;
    if (j.progressoTreino >= 1 && j.overall < 95) {
      j.progressoTreino -= 1;
      j.overall++;
      if (c.humano) avisos.push(`${j.nome} evoluiu no treino (${chave}): força ${j.overall}.`);
    }
  }
  return avisos;
}

export function jogadorDoClube(c: Clube, id: number): Jogador | undefined {
  return c.elenco.find((j) => j.id === id);
}
